import { ButtonVariants, Languages } from '../../constants'
import Hamburguer from '../Atoms/Buttons/Hamburguer/Hamburguer'
import Link from '../Atoms/Link/Link'
import LinkFlag from '../Molecules/LinkFlag'

import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import RoundedButton from 'components/Atoms/Buttons/RoundedButton/RoundedButton'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useLocation } from 'react-router-dom'

export interface HeaderProps {
  selected?: string
  onClick: (section: string) => void
  logo: string
  className?: string
  hideOptions?: boolean
  hideButton?: boolean
  cicloBono?: boolean
  onlySpanish?: boolean
}

const Header = ({
  selected,
  onClick,
  logo,
  className,
  hideOptions,
  hideButton,
  cicloBono,
  onlySpanish,
}: HeaderProps) => {
  const [t, i18n] = useTranslation('global')
  const language = i18n.language
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const mobileDropdownRef = useRef<HTMLDivElement>(null)

  const navClassname = classNames('bg-white', 'p-4', {
    'flex flex-col flex-grow pb-4 md:pb-0 md:flex md:justify-center md:flex-row':
      isOpen,
    'hidden md:flex md:flex-row md:justify-center flex-col md:align-center':
      !isOpen,
  })
  const linkClassname = classNames({
    'ml-7': !isOpen,
  })

  const closeMenu = () => {
    setIsOpen(false)
    setDropdownOpen(false)
    setMobileDropdownOpen(false)
  }

  const location = useLocation()
  const lang = location.search.split('lang=')[1]

  useEffect(() => {
    if (onlySpanish) {
      i18n.changeLanguage('es')
      return
    }

    if (lang === 'en') {
      i18n.changeLanguage('en')
    } else {
      i18n.changeLanguage('es')
    }
  }, [lang, onlySpanish, i18n])

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        isOpen &&
        !event.target.closest('.nav-container') &&
        !event.target.closest('.hamburger-button')
      ) {
        closeMenu()
      }

      if (
        dropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false)
      }

      if (
        mobileDropdownOpen &&
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target)
      ) {
        setMobileDropdownOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen, dropdownOpen, mobileDropdownOpen])

  const languageBtn = () => {
    if (language === Languages.ENGLISH) {
      return Languages.SPANISH
    } else {
      return Languages.ENGLISH
    }
  }

  const handleClick = (section: string) => {
    onClick(section)
    closeMenu()
  }

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen)
  }

  const toggleMobileDropdown = () => {
    setMobileDropdownOpen(!mobileDropdownOpen)
  }

  // Get the base URL for the current environment
  const getBaseUrl = () => {
    // In development, use the current origin
    // In production, this will be the actual domain
    return window.location.origin
  }

  return (
    <div className={className}>
      <div className="antialiased bg-white">
        <div className="w-full text-gray-700 bg-white nav-container">
          <div className="flex flex-col max-w-screen-xl px-4 mx-auto md:items-center md:justify-between md:flex-row md:px-6 lg:px-8">
            <div className="flex flex-row items-center justify-between p-4">
              <RouterLink to="/" aria-label="Ciclogreen" title="Ciclogreen">
                <img
                  alt="Ciclogreen"
                  src={`${process.env.REACT_APP_URL_S3}/images/${logo}`}
                  width={152}
                  height={23}
                />
              </RouterLink>
              <Hamburguer
                className="md:hidden focus:outline-none focus:shadow-outline"
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className={navClassname}>
              {!hideOptions && (
                <>
                  {/* Regular Carpooling Link - Desktop */}
                  {/* <a
                    href="https://ciclogreen.com/certificados-ahorro-energetico-carpooling-corporativo"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="hidden md:flex justify-center"
                  >
                    <Link
                      selected={selected === 'carpooling'}
                      className={linkClassname}
                      onClick={() => {}}
                    >
                      Carpooling
                    </Link>
                  </a> */}

                  {/* Regular Carpooling Link - Mobile */}
                  {/* <a
                    href="https://ciclogreen.com/certificados-ahorro-energetico-carpooling-corporativo"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="md:hidden -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Carpooling
                  </a> */}

                  {/* Carpooling Dropdown - Desktop */}
                  <div
                    className="relative hidden md:flex justify-center"
                    ref={dropdownRef}
                  >
                    <button
                      className="group flex items-center focus:outline-none"
                      onClick={toggleDropdown}
                      aria-expanded={dropdownOpen}
                    >
                      <Link
                        selected={selected === 'carpooling'}
                        onClick={() => {}}
                        className="h-[52px]"
                      >
                        {t('header.carpooling')}
                      </Link>
                      <svg
                        className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${
                          dropdownOpen ? 'rotate-180' : ''
                        } text-gray-700 group-hover:text-blue-800`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    </button>

                    {dropdownOpen && (
                      <div className="absolute top-full left-0 z-10 mt-1 w-64 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {/* <a
                          href={`${getBaseUrl()}/ciclo-bono`}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-800"
                          onClick={() => setDropdownOpen(false)}
                        >
                          CicloBono
                        </a> */}
                        <a
                          href={`${getBaseUrl()}/certificados-ahorro-energetico-carpooling-corporativo`}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-800"
                          onClick={() => setDropdownOpen(false)}
                        >
                          {t('header.carpoolingCompanies')}
                        </a>
                        <a
                          href={`https://landing.ciclogreen.com/universidades-dossier`}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-800"
                          onClick={() => setDropdownOpen(false)}
                        >
                          {t('header.carpoolingUniversities')}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Mobile Dropdown Panel */}
                  <div
                    className="md:hidden -mx-3 w-full"
                    ref={mobileDropdownRef}
                  >
                    <button
                      type="button"
                      onClick={toggleMobileDropdown}
                      className="flex w-full items-center justify-between rounded-lg py-2 px-3 text-base font-semibold text-gray-900 hover:bg-gray-50"
                      aria-expanded={mobileDropdownOpen}
                    >
                      {t('header.carpooling')}
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        className={`h-5 w-5 flex-none transition-transform duration-600 ${
                          mobileDropdownOpen ? 'rotate-180' : ''
                        }`}
                      >
                        <path
                          d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                          clipRule="evenodd"
                          fillRule="evenodd"
                        />
                      </svg>
                    </button>

                    {/* Mobile Dropdown Panel con animación */}
                    <div
                      className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        mobileDropdownOpen
                          ? 'max-h-40 opacity-100'
                          : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="py-2 space-y-2 transform transition-transform duration-500">
                        {/* <a
                          href={`${getBaseUrl()}/ciclo-bono`}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          CicloBono
                        </a> */}
                        <a
                          href={`${getBaseUrl()}/certificados-ahorro-energetico-carpooling-corporativo`}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {t('header.carpoolingCompanies')}
                        </a>
                        <a
                          href={`https://landing.ciclogreen.com/universidades-dossier`}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {t('header.carpoolingUniversities')}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Regular Nav Items */}

                  {/* <Link
                    className={`${linkClassname} hidden md:block`}
                    selected={selected === 'successStories'}
                    onClick={() => handleClick('successStories')}
                  >
                    {t('header.successStories')}
                  </Link>
                  <Link
                    selected={selected === 'benefits'}
                    className={`${linkClassname} hidden md:block`}
                    onClick={() => handleClick('benefits')}
                  >
                    {t('header.benefits')}
                  </Link>
                  <Link
                    selected={selected === 'solutions'}
                    className={`${linkClassname} hidden md:block`}
                    onClick={() => handleClick('solutions')}
                  >
                    {t('header.solutions')}
                  </Link> */}
                  <a
                    href={`${getBaseUrl()}/ciclo-bono`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="hidden md:flex justify-center"
                  >
                    <Link
                      selected={selected === 'cicloBono'}
                      className={linkClassname}
                      onClick={() => {}}
                    >
                      {t('header.cicloBono')}
                    </Link>
                  </a>
                  <a
                    href="https://blog.ciclogreen.com/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="hidden md:flex justify-center"
                  >
                    <Link
                      selected={selected === 'blog'}
                      className={linkClassname}
                      onClick={() => {}}
                    >
                      {t('header.blog')}
                    </Link>
                  </a>

                  <a
                    href={`https://landing.ciclogreen.com/meetings/gregoriomagno/reunionciclogreen?uuid=463b1953-aa0f-48fa-8e84-3fe9f86f3213&utm_source=home&utm_medium=website&utm_campaign=reunion-ciclogreen&utm_content=contacto`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="hidden md:flex justify-center"
                  >
                    <Link
                      selected={selected === 'contact'}
                      className={linkClassname}
                      onClick={() => {}}
                    >
                      {t('header.contact')}
                    </Link>
                  </a>

                  {/* Mobile Regular Items */}
                  <a
                    href={`${getBaseUrl()}/ciclo-bono`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="md:hidden -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    {t('header.cicloBono')}
                  </a>
                  <a
                    className="md:hidden -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    onClick={() => handleClick('successStories')}
                  >
                    {t('header.successStories')}
                  </a>
                  <a
                    className="md:hidden -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    onClick={() => handleClick('benefits')}
                  >
                    {t('header.benefits')}
                  </a>
                  <a
                    className="md:hidden -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    onClick={() => handleClick('solutions')}
                  >
                    {t('header.solutions')}
                  </a>
                  <a
                    href="https://blog.ciclogreen.com/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="md:hidden -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    {t('header.blog')}
                  </a>
                  <a
                    className="md:hidden -mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    onClick={() => handleClick('contact')}
                  >
                    {t('header.contact')}
                  </a>
                </>
              )}

              {/* Language Switcher */}
              {!onlySpanish && (
                <div className="md:ml-10 flex justify-center">
                  <div className="flex flex-row md:ml-3">
                    <LinkFlag
                      language={languageBtn()}
                      height={15}
                      onClick={() => i18n.changeLanguage(`${languageBtn()}`)}
                    />
                  </div>
                </div>
              )}

              {/* Dashboard Button */}
              {!hideButton && !cicloBono && (
                <a
                  className={`${linkClassname} md:mt-0 mt-5 w-full md:w-auto flex justify-center`}
                  href="https://backoffice.cclgrn.com/"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <RoundedButton
                    variant={ButtonVariants.GRADIENT}
                    className={isOpen ? 'mt-2' : 'mt-0'}
                  >
                    {t('btn.dashboardAccess')}
                  </RoundedButton>
                </a>
              )}
              {cicloBono && (
                <a
                  className={`${linkClassname} md:mt-0 mt-5 w-full md:w-auto flex justify-center`}
                  href="https://app.hubspot.com/meetings/gregoriomagno/reunionciclogreen?uuid=0198f842-6cd5-4a00-8d58-d1d19731dbeb&utm_source=ciclobono&utm_medium=email&utm_campaign=reunion-ciclogreen&utm_content=solicitar-demo"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <RoundedButton
                    variant={ButtonVariants.GRADIENT}
                    className={isOpen ? 'mt-2' : 'mt-0'}
                  >
                    {t('btn.contactUs')}
                  </RoundedButton>
                </a>
              )}
            </nav>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Header
